/* oxlint-disable */
"use client"

import Image from "next/image"
import { useRouter } from "next/navigation"
import {
  memo,
  useCallback,
  useMemo,
  useState,
  useSyncExternalStore
} from "react"

import { AiFillDollarCircle } from "react-icons/ai"

import AcceptModal from "@/components/modals/AcceptModal"
import { Button } from "@/components/ui/button"
import { useAuth } from "@/core/auth/client/AuthProvider"
import { GameFavoriteButton } from "@/features/casino/components/GameFavoriteButton"
import { MissingGameCover } from "@/features/casino/components/MissingGameCover"
import { useGameFavorite } from "@/features/casino/hooks/useGameFavorite"
import { useNarrowViewport } from "@/features/casino/hooks/useNarrowViewport"
import {
  gameCardTexts,
  gamePreviewTexts
} from "@/features/casino/localization/texts"
import { routes } from "@/features/v2/constants/routes"
import { useRedirectGame } from "@/features/v2/shared/hooks/casinoHooks/useRedirecGame"
import { PlayIcon } from "@/src/shared/assets/icons"
import { useAuthStore } from "@/stores/userStore"
import { GameFlag } from "@/stories/atoms/gameFlag/GameFlag"
import { isGameCurrencyMismatch } from "@/utils/currency"
import { normalizeImageUrl, pickGameThumbUrl } from "@/utils/game-image"
import { isOptimizableImageUrl } from "@/utils/image-optimizer"

import { GameCardProps } from "./interface"

const GAME_CARD_CAROUSEL_SIZES = "(max-width: 767px) 118px, 187px"
const GAME_CARD_GRID_SIZES = "(max-width: 767px) 118px, 187px"

const emptySubscribe = () => () => {}
const getClientSnapshot = () => true
const getServerSnapshot = () => false

function providerCaption(producer: string) {
  if (producer.trim().toLowerCase() === "origami") {
    return gameCardTexts.powerOriginals
  }

  return producer
}

function GameCard({
  game,
  index = 0,
  priority = false,
  categorySelected = "",
  onGameClick,
  showFavoriteButton = true,
  showCurrencyBadge = true,
  showPlayButton = true,
  variant = "default"
}: Readonly<GameCardProps>): React.ReactElement {
  const narrow = useNarrowViewport()
  const isClient = useSyncExternalStore(
    emptySubscribe,
    getClientSnapshot,
    getServerSnapshot
  )
  const imageKey = `${game.ai_thumb_url ?? ""}|${game.thumb_url ?? ""}`
  const [failedImage, setFailedImage] = useState({ key: imageKey, count: 0 })
  const imageFailures = failedImage.key === imageKey ? failedImage.count : 0
  const [currencyModalData, setCurrencyModalData] = useState<{
    url: string
    originalCurrency: string
    fallbackCurrency: string
  } | null>(null)
  const [isCurrencyModalOpen, setIsCurrencyModalOpen] = useState(false)
  const { handleGameRedirect } = useRedirectGame()
  const router = useRouter()
  const { session } = useAuth()
  const [isHovered, setIsHovered] = useState(false)
  const { account } = useAuthStore()
  const { isFavorite } = useGameFavorite()
  const userCurrency = account?.currency || "USD"

  const handleImageError = useCallback((): void => {
    setFailedImage((current) => {
      const count = current.key === imageKey ? current.count : 0
      return { key: imageKey, count: count >= 2 ? count : count + 1 }
    })
  }, [imageKey])

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true)
  }, [])

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false)
  }, [])

  const handleGameClick = useCallback(
    (gameItem: any, gameIndex: number) => {
      if (onGameClick) {
        onGameClick(gameItem, gameIndex, categorySelected)
        return
      }

      if (!session?.user) {
        router.push(routes.LOGIN)
        return
      }

      const patchedGame = {
        ...gameItem,
        released_at: gameItem.released_at ?? undefined
      }

      handleGameRedirect({
        item: patchedGame,
        categorySelected,
        gameIndex,
        onCurrencyMismatch: ({ url, originalCurrency, fallbackCurrency }) => {
          setCurrencyModalData({ url, originalCurrency, fallbackCurrency })
          setIsCurrencyModalOpen(true)
        }
      })
    },
    [onGameClick, categorySelected, router, session, handleGameRedirect]
  )

  const handleActivate = useCallback(() => {
    handleGameClick(game, index)
  }, [game, handleGameClick, index])

  const cardActsAsButton = narrow || !showPlayButton

  const handleCardKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!cardActsAsButton) {
        return
      }

      if (event.key !== "Enter" && event.key !== " ") {
        return
      }

      event.preventDefault()
      handleActivate()
    },
    [cardActsAsButton, handleActivate]
  )

  const sectionInfo = useMemo(() => {
    if (!game.sections || Object.keys(game.sections).length === 0) {
      return { color: null, text: null }
    }

    if (game.sections.recomendados) {
      return { color: "#f54180", text: "TOP" }
    }
    if (game.sections.populares) {
      return { color: "#c4841d", text: "Populares" }
    }
    if (game.sections.nuevos) {
      return { color: "#17c964", text: "Nuevos" }
    }

    return { color: null, text: null }
  }, [game.sections])

  const imageUrl = useMemo(() => {
    const thumb = pickGameThumbUrl(
      { ai_thumb_url: game.ai_thumb_url, thumb_url: game.thumb_url },
      imageFailures
    )
    return thumb ? normalizeImageUrl(thumb) : null
  }, [imageFailures, game.ai_thumb_url, game.thumb_url])

  const imageSizes =
    variant === "carousel" ? GAME_CARD_CAROUSEL_SIZES : GAME_CARD_GRID_SIZES
  const caption = providerCaption(game.producer)
  const favorite = isFavorite(game.identifier)

  const cardClasses =
    variant === "preview"
      ? "group relative w-full min-w-0 rounded-lg"
      : variant === "carousel"
        ? "group relative rounded-lg overflow-visible hover:shadow-lg w-[118px] md:w-[187px]"
        : "group relative rounded-lg overflow-visible hover:shadow-lg transition-all duration-300 w-full"

  const imageClasses =
    variant === "preview"
      ? "relative aspect-107/133 w-full overflow-hidden rounded-lg bg-neutral-900"
      : "relative overflow-hidden bg-neutral-900 w-full rounded-lg aspect-118/146 md:aspect-187/231"
  const hasCurrencyMismatch = useMemo(() => {
    return isGameCurrencyMismatch(userCurrency, game.currency)
  }, [userCurrency, game.currency])

  return (
    <>
      {currencyModalData && (
        <AcceptModal
          url={currencyModalData.url}
          originalCurrency={currencyModalData.originalCurrency}
          fallbackCurrency={currencyModalData.fallbackCurrency}
          isOpen={isCurrencyModalOpen}
          onOpenChange={setIsCurrencyModalOpen}
        />
      )}
      <div
        className={`${cardClasses} cursor-pointer`}
        role={cardActsAsButton ? "button" : undefined}
        tabIndex={cardActsAsButton ? 0 : undefined}
        aria-label={
          cardActsAsButton
            ? `${gamePreviewTexts.openGame}: ${game.title}`
            : undefined
        }
        onClick={handleActivate}
        onKeyDown={handleCardKeyDown}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          transform:
            variant === "carousel" && isHovered
              ? "scale(1.05)"
              : (undefined as any),
          transition: "transform 0.3s ease-in-out",
          transformOrigin: "center",
          padding: variant === "carousel" && isHovered ? "4.5px" : ""
        }}
      >
        <div className={imageClasses}>
          {imageUrl ? (
            <Image
              src={imageUrl}
              alt={game.title}
              fill
              className="object-cover rounded-lg"
              onError={handleImageError}
              sizes={imageSizes}
              priority={priority}
              fetchPriority={priority ? "high" : "auto"}
              loading={priority ? "eager" : "lazy"}
              decoding="async"
              unoptimized={!isOptimizableImageUrl(imageUrl)}
            />
          ) : (
            <MissingGameCover />
          )}

          {showPlayButton && (
            <div className="pointer-events-none absolute inset-0 hidden rounded-lg bg-black/40 opacity-0 backdrop-blur-[4px] transition-opacity duration-300 xlg:block xlg:group-hover:pointer-events-auto xlg:group-hover:opacity-100">
              <div className="absolute inset-x-0 top-[51px] flex flex-col items-center px-2 md:top-[81px]">
                <Button
                  type="button"
                  aria-label={`${gameCardTexts.play}: ${game.title}`}
                  variant="hand"
                  size="sketch"
                  className="w-[104px]"
                  onClick={(event) => {
                    event.stopPropagation()
                    handleGameClick(game, index)
                  }}
                >
                  {gameCardTexts.play}
                  <PlayIcon className="ml-1.5 size-5 shrink-0" aria-hidden />
                </Button>
                <p className="mt-2 w-full truncate text-center text-[14px] font-black leading-none text-white">
                  {game.title}
                </p>
                {caption ? (
                  <p className="mt-0.5 w-full truncate text-center text-[14px] font-normal leading-none text-white">
                    {caption}
                  </p>
                ) : null}
              </div>
            </div>
          )}

          {isClient && session && showFavoriteButton && (
            <GameFavoriteButton
              gameCode={game.identifier}
              className={`absolute top-2.5 right-2.5 z-30 hidden size-6 p-0 xlg:block ${favorite ? "opacity-100" : "pointer-events-none text-white/75 opacity-0 transition-opacity xlg:group-hover:pointer-events-auto xlg:group-hover:opacity-100"}`}
            />
          )}
        </div>

        {isClient && showCurrencyBadge && hasCurrencyMismatch && (
          <div className="absolute -top-2 -right-2 z-30 rounded-full bg-black/80 p-0.5 shadow-md">
            <AiFillDollarCircle className="text-xl text-yellow-400 drop-shadow-xs" />
          </div>
        )}
        {sectionInfo.color && sectionInfo.text && (
          <div className="absolute top-0 left-1">
            <GameFlag color={sectionInfo.color} text={sectionInfo.text} />
          </div>
        )}
      </div>
    </>
  )
}

export default memo(GameCard)
